/* eslint-disable react/prop-types */
import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
} from 'react';
import {
  Form,
  Spin,
  Row,
  Col,
  Input,
  Button,
  Space,
  Grid,
  Result,
  DatePicker,
  Tooltip,
  message,
} from 'antd';
import { getTramitesIndexados, patchTramite } from 'api/tramites/tramites';
import { useSelector } from 'react-redux';
import { ActionsNames, isAdmin } from 'utils/permission';
import {
  capitalize,
  getDistinctValues,
  toMoment,
  toUpper,
} from 'utils/normalizers';
import Table from 'components/Table';
import Select from 'components/Select';
import {
  DeleteOutlined,
  CarOutlined,
  FilterOutlined,
  InboxOutlined,
  RightOutlined,
  UserOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
  DoubleRightOutlined,
} from '@ant-design/icons';
import { getCatalogoDeErroresWS, getContenTypePorTipoDePadron } from 'api/catalogos';
import { getDepartamentos } from 'api/configuracion/departamentos';
import { getDependencias } from 'api/configuracion/dependencias';
import padronToString from 'utils/padronesToString';
import { DATE_FORMAT, formatReceived } from 'utils/formatValues';
import ModalAfectacionPadron from 'components/ModalAfectacionPadron';
import { getConfiguracionesDePadrones } from 'api/configuracion/configuracionPadrones';
import { getLogsWS, getEstatusRepuve, getPasosDeTramites } from 'api/tramites';
import { postEnviarInformacionWS, postMasivoEnviarInformacionWS } from 'api/recaudacion/repuve';
import ModalDireccion from 'components/Direccion/Modal';
import { baseURI } from 'api/empresas/contribuyentes';
import { getPermissionActionsConfigsPadrones } from 'utils/permission/models';
import { getVehiculoCaja } from 'api/recaudacion';
import { getUsuarios } from 'api/usuarios/usuarios';
import { tiposDePasosEnum, estatusEnvioWs } from 'views/Tramites/GPM/enums';

export const permissionConfig = {
  mainPermission: 'seguimiento_tramites_repuve',
  permissions: [
    'view_tramite',
    'view_catalogosdeusosdeconfiguraciondepadron',
    'view_unidadresponsable',
    'view_unidadoperativa',
    'view_configuraciondepadron',
    'view_pasoparatramite',
    'view_logswsrepuve',
    'view_estatusenviowsrepuve',
  ],
  customActions: [
    {
      name: 'enviar_masivo_ws',
      title: 'Enviar masivo a WS',
      permissions: ['enviar_vehiculos_ws'],
    },
    {
      name: 'enviar_ws',
      title: 'Enviar a WS',
      permissions: ['enviar_vehiculo_ws'],
    },
    ...getPermissionActionsConfigsPadrones(ActionsNames.change)
      .filter((c) => /vehiculo|contribuyente/i.test(c.name))
      .map((c) => ({ ...c, permissions: [...c.permissions, 'change_tramite'] })),
    {
      name: 'change_direccion',
      title: 'Registrar/Modificar dirección',
      permissions: [
        'add_direccion',
        'change_direccion',
        'view_catalogodepais',
        'view_estado',
        'view_municipio',
        'view_codigopostal',
        'view_tipodevialidad',
      ],
    },
  ],
};

const SeguimientoDeTramitesRepuve = ({ permission }) => {
  const screens = Grid.useBreakpoint();
  const user = useSelector(({ auth }) => auth.user);
  const searchInputRef = useRef();
  const [formFilter] = Form.useForm();
  const tipoDeFiltro = Form.useWatch('tipoDeFiltro', formFilter);
  const filtro = Form.useWatch('filtro', formFilter);
  const nombre_solicitante = Form.useWatch('nombre_solicitante', formFilter);
  const apellido_solicitante = Form.useWatch('apellido_solicitante', formFilter);
  const folio = Form.useWatch('folio', formFilter);
  const [loading, setLoading] = useState(true);
  const [visibleEdit, setVisibleEdit] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [pasosParaTramites, setPasosParaTramites] = useState([]);
  const [estatusRepuve, setEstatusRepuve] = useState([]);
  const [catalogoErrores, setCatalogoErrores] = useState([]);

  const _isAdmin = useMemo(() => isAdmin(), []);

  const params = useMemo(() => {
    const _params = { estados_globales: 4, content_type_a_padron: getContenTypePorTipoDePadron(4) };
    if (!_isAdmin) {
      _params.usuario_creo = user.id;
      if (user.centro_de_costos) {
        _params.unidad_de_recaudacion = user.unidad_de_recaudacion;
      } else if (user.unidad_responsable) {
        _params.departamento = user.unidad_responsable.id;
      } else if (user.unidad_operativa) {
        _params.dependencia = user.unidad_operativa.id;
        delete _params.usuario_creo;
      } else return null;
    }
    if (tipoDeFiltro && (filtro || (nombre_solicitante && apellido_solicitante))) {
      if (tipoDeFiltro === 'fecha_de_inicio') {
        _params[tipoDeFiltro] = filtro.format(DATE_FORMAT);
      } else if (tipoDeFiltro === 'nombre_solicitante') {
        _params.nombre_solicitante = nombre_solicitante;
        _params.apellido_solicitante = apellido_solicitante;
      } else if (filtro) {
        _params[tipoDeFiltro] = filtro;
      }
    }
    if (folio) {
      _params.folio = folio;
    }
    return _params;
  }, [folio, tipoDeFiltro, filtro, nombre_solicitante, apellido_solicitante]);

  const [unidadesResponsables, setUnidadesResponsables] = useState([]);
  const [unidadesOperativas, setUnidadesOperativas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [configContribuyente, setConfigContribuyente] = useState();
  const [configVehiculo, setConfigVehiculo] = useState();
  const selected = data.results.find((e) => e.id === selectedRowKeys[0]);

  const focusSearchInput = () => setTimeout(() => searchInputRef.current?.focus());

  const fetchData = async (page = 1, _params) => {
    setLoading(true);
    const filters = _params || params;
    if (filters.numero_de_identificacion_vehicular) {
      const [vehiculo] = await getVehiculoCaja({
        numero_de_identificacion_vehicular: filters.numero_de_identificacion_vehicular,
      });
      if (vehiculo) {
        filters.object_id_padron = vehiculo.id;
      } else {
        message.warn('No se encontrón vehículo con NIV brindado');
        setLoading(false);
        return;
      }
    }
    const _data = await getTramitesIndexados({ ...filters, page });
    _data.page = page;
    const ids = getDistinctValues(_data.results.map((e) => e.id));
    const logs = await Promise.all(ids.map((tramite) => getLogsWS({ tramite })));
    _data.results = _data.results.map((e, idx) => ({ ...e, logs: logs[idx] }));
    _data.params = filters;
    setData(_data);
    setLoading(false);
    setSelectedRowKeys([]);
  };

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const _pasosParaTramites = (await Promise.all([
        getPasosDeTramites({ tipo_de_paso: tiposDePasosEnum.ALTA_AFECTADO }),
        getPasosDeTramites({ tipo_de_paso: tiposDePasosEnum.MODIFICACION_AFECTADO }),
      ])).flat();
      const _usuarios = await getUsuarios();
      const promises = [
        getEstatusRepuve(),
        getCatalogoDeErroresWS(),
        getConfiguracionesDePadrones({ padron: 15, uso_de_configuracion_de_padron: 1 }),
        getConfiguracionesDePadrones({ padron: 4, uso_de_configuracion_de_padron: 1 }),
      ];
      if (_isAdmin) {
        promises.push(getDepartamentos());
        promises.push(getDependencias());
      } else if (!user.unidad_responsable && user.unidad_operativa) {
        promises.push(getDepartamentos({ unidad_operativa: user.unidad_operativa?.id }));
      }
      if (!promises.length) return;
      const [
        _estatusRepuve,
        _catalogoErrores = [],
        [_configContribuyente],
        [_configVehiculo],
        _unidadesResponsables = [],
        _unidadesOperativas = [],
      ] = await Promise.all(promises);
      setUsuarios(_usuarios);
      setEstatusRepuve(_estatusRepuve);
      setCatalogoErrores(_catalogoErrores);
      setConfigContribuyente(_configContribuyente);
      setConfigVehiculo(_configVehiculo);
      setUnidadesResponsables(_unidadesResponsables);
      setUnidadesOperativas(_unidadesOperativas);
      setPasosParaTramites(_pasosParaTramites);
      focusSearchInput();
      setLoading(false);
      if (params) formFilter.submit();
    };
    if (!data.results.length) fetch();
  }, []);

  const tiposDeFiltros = [
    {
      id: 0,
      descripcion: 'Todo',
    },
    {
      id: 'dependencia',
      descripcion: 'Dependencia',
      visible: _isAdmin,
    },
    {
      id: 'departamento',
      descripcion: 'Departamento',
      visible: _isAdmin || !!params.dependencia,
    },
    {
      id: 'usuario_creo',
      descripcion: 'Usuario creo',
      visible: _isAdmin,
    },
    {
      id: 'nombre_solicitante',
      descripcion: 'Nombre de solicitante',
    },
    {
      id: 'descripcion',
      descripcion: 'Trámite',
    },
    {
      id: 'fecha_de_inicio',
      descripcion: 'Fecha de inicio',
    },
    {
      id: 'estatus_repuve',
      descripcion: 'Estatus',
    },
    {
      id: 'numero_de_identificacion_vehicular',
      descripcion: 'NIV',
    },
    {
      id: 'codigo_error',
      descripcion: 'Código de Error',
    },
  ].filter((e) => ('visible' in e ? e.visible : true));

  const filtros = {
    0: null,
    dependencia: unidadesOperativas,
    departamento: unidadesResponsables,
    nombre_solicitante: null,
    descripcion: null,
    fecha_de_inicio: null,
    estatus_repuve: estatusRepuve,
    numero_de_identificacion_vehicular: null,
    codigo_error: catalogoErrores,
    usuario_creo: usuarios,
  };

  const columns = [
    {
      titleText: 'Folio',
      width: 200,
      dataIndex: 'folio',
      key: 'folio',
    },
    {
      titleText: 'Trámite',
      width: 400,
      dataIndex: 'descripcion',
      key: 'descripcion',
    },
    {
      titleText: 'Solicitante',
      width: 300,
      dataIndex: 'solicitante',
      key: 'solicitante',
      render: (_, r) => padronToString(
        r.ciudadano || r.contribuyente,
        r.solicitante_ciudadano ? 1 : 15,
      ),
    },
    {
      titleText: 'NIV',
      width: 200,
      dataIndex: 'niv',
      key: 'niv',
      render: (_, r) => r.padron?.numero_de_identificacion_vehicular,
    },
    {
      titleText: 'Fecha Registro',
      width: 200,
      dataIndex: 'fecha_de_inicio',
      key: 'fecha_de_inicio',
      render: (val) => toMoment(val).format(),
    },
    {
      titleText: 'Estatus',
      width: 200,
      dataIndex: 'estatus_repuve',
      key: 'estatus_repuve',
      render: (estatus_repuve, record) => {
        const { error, codigo_error } = record.logs.at(-1) || {};
        if (!error) return estatusRepuve.find((e) => e.id === estatus_repuve)?.descripcion;
        return (
          <>
            {estatusRepuve.find((e) => e.id === estatus_repuve)?.descripcion}
            {error && (
              <Tooltip title={`${codigo_error} - ${error}`}>
                <QuestionCircleOutlined style={{ paddingLeft: 5 }} className="warn" />
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
    onChange: (_selectedRowKeys) => setSelectedRowKeys(_selectedRowKeys),
  };

  const handleOnRowClick = (record) => setSelectedRowKeys([record.id]);

  if (!params) {
    return (
      <Row align="middle" justify="center" style={{ height: '100%' }}>
        <Result
          title="Sin trámites para mostrar"
          icon={<InboxOutlined className="warn" />}
          subTitle={<b>Usuario no cuenta con departamento o dependencia</b>}
        />
      </Row>
    );
  }

  const labelFiltro = tiposDeFiltros.find((tf) => tf.id === tipoDeFiltro)?.descripcion;

  const pasosSelecionado = selected ? pasosParaTramites
    .filter((p) => p.plantilla_de_tramite === selected.plantilla)
    .sort((a, b) => a.orden - b.orden) : [];

  const getProps = () => {
    if (visibleEdit === 'vehiculo') {
      const configuracionDePadron = pasosSelecionado[0]?.configuracion_de_padron || configVehiculo;
      return {
        configuracionDePadron,
        label: 'Modificación afectado',
        padron: formatReceived(selected.padron),
        padronSolicitante: selected.contribuyente,
      };
    }

    return {
      configuracionDePadron: configContribuyente,
      label: 'Modificación solicitante',
      padron: selected.contribuyente,
    };
  };

  const callback = async (_, enviar) => {
    setVisibleEdit(false);
    const [idTramite] = selectedRowKeys;
    const updated = await patchTramite(idTramite, { estatus_repuve: estatusEnvioWs.PENDIENTE });
    if (!updated) return;
    await fetchData(params.page, data.params);
    if (enviar) setTimeout(() => postEnviarInformacionWS(idTramite));
  };

  const disabled = [estatusEnvioWs.ERROR_NO_PROCEDE, estatusEnvioWs.ACTUALIZADO]
    .includes(selected?.estatus_repuve);

  const valueProp = {
    codigo_error: 'numero_de_error',
  }[tipoDeFiltro];

  const labelProp = {
    usuario_creo: 'nombre_completo',
  }[tipoDeFiltro];

  const keyProp = {
    codigo_error: 'numero_de_error',
    usuario_creo: 'numero_de_empleado',
  }[tipoDeFiltro];

  return (
    <Spin tip="Cargando" spinning={loading}>
      <Form
        onFinish={() => fetchData()}
        form={formFilter}
        layout="vertical"
        initialValues={{ tipoDeFiltro: 0 }}
        name="GPM"
      >
        <Row gutter={10}>
          <Col xs={24} sm={24} md={8}>
            <Form.Item
              label="Búsqueda por folio"
              name="folio"
              tooltip="Búsqueda de trámite"
              normalize={capitalize}
            >
              <Input allowClear ref={searchInputRef} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Form.Item label="Filtrar por" name="tipoDeFiltro" tooltip="Filtrado de trámites disponibles">
              <Select
                dataSource={tiposDeFiltros}
                onChange={() => formFilter
                  .setFieldsValue({ filtro: null, nombre_solicitante: null })}
                allowClear={false}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={8} hidden={!tipoDeFiltro}>
            {Array.isArray(filtros[tipoDeFiltro]) && (
              <Form.Item label={labelFiltro} name="filtro" rules={[{ required: true }]}>
                <Select
                  disabled={!tipoDeFiltro}
                  dataSource={filtros[tipoDeFiltro]}
                  valueProp={valueProp}
                  keyProp={keyProp}
                  labelProp={labelProp}
                  keyLabelRender={!!keyProp}
                  allowClear={false}
                />
              </Form.Item>
            )}
            {tipoDeFiltro === 'fecha_de_inicio' && (
              <Form.Item label={labelFiltro} name="filtro" rules={[{ required: true }]}>
                <DatePicker
                  format="DD/MM/YYYY"
                  disabledDate={(date) => date.isAfter(new Date())}
                />
              </Form.Item>
            )}
            {['nombre_solicitante', 'descripcion', 'numero_de_identificacion_vehicular'].includes(tipoDeFiltro) && (
              <Form.Item
                label={tipoDeFiltro !== 'nombre_solicitante' ? labelFiltro : 'Nombre'}
                name={tipoDeFiltro !== 'nombre_solicitante' ? 'filtro' : 'nombre_solicitante'}
                rules={[{ required: true }]}
                normalize={toUpper}
              >
                <Input />
              </Form.Item>
            )}
          </Col>
          {tipoDeFiltro === 'nombre_solicitante' && (
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                label="Apellido"
                name="apellido_solicitante"
                rules={[{ required: true }]}
                normalize={toUpper}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} sm={24} md={8}>
            <Form.Item style={{ marginTop: screens.md ? 22 : 0 }}>
              <Space>
                <Button htmlType="submit">
                  Filtrar
                  <FilterOutlined />
                </Button>
                <Button
                  onClick={() => setTimeout(() => formFilter.submit())}
                  htmlType="reset"
                >
                  Limpiar
                  <DeleteOutlined />
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Table
        data={data.results}
        cols={columns}
        handleOnRowClick={handleOnRowClick}
        rowSelection={rowSelection}
        allowSort={false}
        allowSearch={false}
        pagination={false}
        allowColumnSelector={false}
        paginationCallback={(page) => fetchData(page)}
        expandable={false}
        count={data.count}
        currentPage={data.page}
        restHeigth={screens.md ? 100 : 180}
        rowKey="id"
        emptyText={params ? null : 'Usuario no cuenta con departamento o dependencia'}
        customActions={[
          {
            icon: <DoubleRightOutlined />,
            text: 'Realizar envío masivo',
            onClick: async () => postMasivoEnviarInformacionWS(),
            hasPermission: _isAdmin && permission.enviar_masivo_ws,
            disabledBySelection: false,
          },
          {
            icon: <EyeOutlined />,
            text: 'Ver detalle',
            disabled: /migrado/i.test(selected?.comentarios_del_tramite) || selected?.procedencia === 1,
            href: `/tramites/registrar-tramite/?tramite=${selectedRowKeys[0]}&finalizados=1`,
            target: 'blank',
          },
          {
            icon: <RightOutlined />,
            text: 'Realizar envío',
            onClick: async () => {
              const sent = await postEnviarInformacionWS(selectedRowKeys[0]);
              if (sent) fetchData(params.page, data.params);
            },
            disabled: selected?.estatus_repuve === estatusEnvioWs.ACTUALIZADO,
            hasPermission: permission.enviar_ws,
          },
          {
            icon: <EnvironmentOutlined />,
            text: 'Editar dirección',
            onClick: () => setVisibleEdit('direccion'),
            hasPermission: permission.change_direccion,
            disabled,
          },
          {
            icon: <UserOutlined />,
            text: 'Editar solicitante',
            onClick: () => setVisibleEdit('solicitante'),
            disabled: !configContribuyente || disabled,
            hasPermission: permission.change_contribuyente,
            tooltip: !configContribuyente ? 'No se encontró configuración de padrón' : null,
          },
          {
            icon: <CarOutlined />,
            text: 'Editar vehículo',
            onClick: () => setVisibleEdit('vehiculo'),
            hasPermission: permission.change_vehiculo,
            disabled: disabled || (!pasosSelecionado.length && !configVehiculo),
            tooltip: !pasosSelecionado.length && !configVehiculo ? 'No se encontró configuración de padrón' : null,
          },
        ]}
        hasAllViewPermission
        mobileColIndex={0}
      />
      {visibleEdit && visibleEdit !== 'direccion' && (
        <ModalAfectacionPadron
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...getProps()}
          visible
          setVisible={setVisibleEdit}
          callback={callback}
          onCancel={() => setVisibleEdit(false)}
          hasPermissionSendWS={permission.enviar_ws}
        />
      )}
      {visibleEdit === 'direccion' && (
        <ModalDireccion
          visible
          callback={callback}
          baseURI={baseURI}
          addressPropName="direcciones"
          defaultVisibleDireccion
          parentID={selected.contribuyente.id}
          onCancel={() => setVisibleEdit()}
          allowMap={false}
          submitControlsWithSentWS={permission.enviar_ws}
        />
      )}
    </Spin>
  );
};

export default SeguimientoDeTramitesRepuve;
